// src/stores/useMedicalRequestStore.ts
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { RequestReporterType, ProxyDetails, PatientDetails } from '../types';
import { submitAdminRequest } from '../services/admin-request.service';
import { useVerifyPatientDetails } from './useVerifyPatientDetails';
import { useVerifyNhsLogin } from './useVerifyNhsLogin';

export const useAdminRequestStore = defineStore('admin-request', () => {
  const expiryTime = ref<Date | null>(null); // +1 hour

  const apiLoading = ref<boolean>(false);
  const setApiLoading = () => (apiLoading.value = true);
  const setApiDone = () => (apiLoading.value = false);

  const isEmergency = ref<boolean>(false);
  const reporterType = ref<RequestReporterType | null>(null);
  const isValidPatient = ref<boolean>(false);
  const patientDetails = ref<PatientDetails | null>(null);
  // unique after here, above can be made modular and shared

  const proxyDetails = ref<ProxyDetails | null>();
  const isVerified = ref<boolean>(false);
  const isNhsVerified = ref<boolean>(false);

  const adminRequest = ref<string>('');
  const attachments = ref<File[]>([]);

  const isExpired = computed(() => Date.now() > expiryTime?.value?.getTime());
  const isProxy = computed(() => reporterType.value === 'PROXY');

  function startNew() {
    adminRequest.value = '';
    attachments.value = [];
    expiryTime.value = new Date(Date.now() + 60 * 60 * 1000); // +1 hour
    isEmergency.value = false;
    isNhsVerified.value = false;
    isValidPatient.value = false;
    isVerified.value = false;
    patientDetails.value = null;
    proxyDetails.value = null;
    reporterType.value = null;
  }

  function endCurrent() {
    startNew();
    expiryTime.value = null;
  }

  function setIsEmergency(value: boolean) {
    isEmergency.value = value;
  }

  function setIsValidPatient(value: boolean) {
    isValidPatient.value = value;
  }

  function setIsNhsVerified(value: boolean = true) {
    isNhsVerified.value = value;
  }

  function setReporterType(value: RequestReporterType) {
    reporterType.value = value;
  }

  function setProxyDetails(details: ProxyDetails | null) {
    proxyDetails.value = details;
  }

  const verifyPatientDetails = useVerifyPatientDetails({
    patientDetails,
    isProxy: isProxy,
    setApiLoading,
    setApiDone,
    setIsValidPatient,
  });

  const verifyNhsLogin = async (code: string, csrfToken: string) => {
    const run = useVerifyNhsLogin({
      code,
      csrfToken,
      setApiLoading,
      setApiDone,
      setIsValidPatient,
      setIsNhsVerified,
      setPatientDetails,
    });

    const result = await run();
    if (result.ok) {
      setReporterType('PATIENT');
    }
    return result;
  };

  async function submitAction() {
    setApiLoading();
    try {
      const result = await submitAdminRequest({
        reporterType: reporterType.value,
        proxyDetails: proxyDetails.value,
        patientDetails: patientDetails.value,
        adminRequest: adminRequest.value,
        attachments: attachments.value,
      });

      if (!result.ok) {
        return {
          ok: false,
          errors: result.errors,
        };
      }

      endCurrent();
      return { ok: true };
    } catch (e) {
      console.error('submitRepeatPrescriptionRequestApi error', e);

      return { ok: false, errors: e.data?.errors };
    } finally {
      setApiDone();
    }
  }

  function setPatientDetails(details: PatientDetails | null) {
    patientDetails.value = details;
  }

  function setAdminRequest(text: string) {
    adminRequest.value = text;
  }

  function addAttachment(file: File) {
    if (attachments.value.length >= 10) return;
    attachments.value.push(file);
  }

  function addAttachments(files: File[]) {
    attachments.value = files;
  }

  function removeAttachment(index: number) {
    attachments.value.splice(index, 1);
  }

  function setNhsSsoDetails(patientDetails: PatientDetails) {
    setIsNhsVerified(true);
    setPatientDetails(patientDetails);
    setIsValidPatient(true);
    setReporterType('PATIENT');
  }

  // ---- Expose flat state ----
  return {
    // apiLoading,
    apiLoading,
    // state
    expiryTime,

    isEmergency,
    reporterType,

    proxyDetails,
    patientDetails,
    isValidPatient,
    isVerified,
    isNhsVerified,
    isProxy,
    adminRequest,
    attachments,

    isExpired,

    // actions
    addAttachment,
    addAttachments,
    endCurrent,
    removeAttachment,
    setAdminRequest,
    setIsEmergency,
    setNhsSsoDetails: setNhsSsoDetails,
    setIsNhsVerified,
    setPatientDetails,
    setProxyDetails,
    setReporterType,
    startNew,
    submitAction,
    //
    verifyPatientDetails,
    verifyNhsLogin,
  };
});
